<script setup>
import { computed, ref } from 'vue'
import { TEXT_GROUPS, BROADCAST_TEXT_FIELDS, BROADCAST_TEXT_MAX, defaultText, AUDIENCE_PRESETS, normalizeAudience } from '@/components/Sites/broadcastAudience'

// Theme › Texts on a broadcast site (runtime `broadcastTexts`, v0.19): every
// string of the Broadcast page, grouped. An empty field shows the wording of
// the audience preset, which is also the placeholder.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	audience: {
		type: String,
		default: 'church',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})
const texts = defineModel({ type: Object, required: true })

const query = ref('')
const presetTitle = computed(() => AUDIENCE_PRESETS.find((p) => p.id === normalizeAudience(props.audience))?.title || '')
const matches = (field) => {
	const q = query.value.trim().toLowerCase()
	if (!q) return true
	const group = TEXT_GROUPS.find((g) => g.id === field.group)?.title || ''
	return `${defaultText(field, props.audience)} ${field.where || ''} ${group} ${texts.value[field.key] || ''}`.toLowerCase().includes(q)
}
const groups = computed(() => TEXT_GROUPS.map((g) => {
	const fields = BROADCAST_TEXT_FIELDS.filter((f) => f.group === g.id)
	return { ...g, fields: fields.filter(matches), overridden: fields.filter((f) => String(texts.value[f.key] || '').trim()).length }
}).filter((g) => g.fields.length))
const set = (key, value) => { texts.value = { ...texts.value, [key]: value } }
const tooLong = (key) => String(texts.value[key] || '').trim().length > BROADCAST_TEXT_MAX
</script>

<template>
	<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="theme-broadcast-texts">
		<div class="mb-3 flex flex-wrap items-end justify-between gap-3">
			<p class="mb-0 min-w-0 flex-1 text-tiny text-surface-7">
				Empty fields use the <span class="text-surface-text-1">{{ presetTitle }}</span> wording shown in grey.
				Change the preset under <router-link :to="{ name: 'SiteAudience', params: { siteId } }" class="underline" data-testid="texts-audience-link">Settings › Audience</router-link>.
				Placeholders like <code class="text-surface-8">{name}</code> or <code class="text-surface-8">{amount}</code> are filled in for viewers.
			</p>
			<input v-model="query" type="search" placeholder="Find a label…" aria-label="Find a label"
				class="w-full rounded-md border border-surface-5 bg-surface-3 px-3 py-1.5 text-sm text-white placeholder:text-surface-6 focus:border-main focus:outline-none sm:w-56" data-testid="texts-search">
		</div>
		<div class="flex flex-col gap-2">
			<details v-for="(g, gi) in groups" :key="g.id" class="rounded-lg bg-surface-3" :open="gi === 0 || Boolean(query.trim())" :data-testid="`texts-group-${g.id}`">
				<summary class="flex cursor-pointer select-none items-center justify-between gap-3 px-3 py-2.5 text-sm font-medium text-white">
					<span>{{ g.title }}</span>
					<span class="text-tiny font-normal text-surface-7">{{ g.overridden ? `${g.overridden} customised` : g.description }}</span>
				</summary>
				<div class="flex flex-col gap-2 border-t border-surface-4 px-3 py-3">
					<div v-for="f in g.fields" :key="f.key" class="texts-row min-w-0" :data-testid="`texts-field-${f.key}`">
						<label :for="`texts-${f.key}`" class="mb-1 block truncate text-tiny text-surface-7" :title="f.where ? `${defaultText(f, audience)} · ${f.where}` : defaultText(f, audience)">
							{{ defaultText(f, audience) }}<span v-if="f.where" class="text-surface-6" :data-testid="`texts-where-${f.key}`"> · {{ f.where }}</span>
						</label>
						<input :id="`texts-${f.key}`" :value="texts[f.key] || ''" type="text" :placeholder="defaultText(f, audience)" :maxlength="BROADCAST_TEXT_MAX + 20" :disabled="disabled"
							class="w-full rounded-md border bg-surface-2 px-3 py-1.5 text-sm text-white placeholder:text-surface-6 focus:border-main focus:outline-none"
							:class="tooLong(f.key) ? 'border-error' : 'border-surface-5'" @input="set(f.key, $event.target.value)">
						<p v-if="tooLong(f.key)" class="mb-0 mt-1 text-tiny text-error">Up to {{ BROADCAST_TEXT_MAX }} characters</p>
					</div>
				</div>
			</details>
			<p v-if="!groups.length" class="mb-0 px-1 text-tiny text-surface-7">No label matches “{{ query }}”.</p>
		</div>
	</section>
</template>
